import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { backend_url } from "../../App";
import {
  showLoadingScreen,
  setSnackBar,
} from "../../reusable_components/site_data/siteDataSlice";

// Helper for auth headers
const getAuthHeader = () => ({
  Authorization: `Token ${localStorage.getItem("token")}`,
});

// Async Thunks
export const fetchAlarmDevices = createAsyncThunk(
  "alarm/fetchAlarmDevices",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.get(
        `${backend_url}/core/api/alarm-devices/`,
        {
          headers: getAuthHeader(),
          params,
        },
      );
      console.log("response of alarm devices", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to fetch alarm devices",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const createAlarmDevice = createAsyncThunk(
  "alarm/createAlarmDevice",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.post(
        `${backend_url}/core/api/alarm-devices/`,
        data,
        {
          headers: getAuthHeader(),
        },
      );
      dispatch(setSnackBar("Alarm device created successfully"));
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to create alarm device",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const updateAlarmDevice = createAsyncThunk(
  "alarm/updateAlarmDevice",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.patch(
        `${backend_url}/core/api/alarm-devices/${id}/`,
        data,
        {
          headers: getAuthHeader(),
        },
      );
      dispatch(setSnackBar("Alarm device updated successfully"));
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to update alarm device",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const deleteAlarmDevice = createAsyncThunk(
  "alarm/deleteAlarmDevice",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      await axios.delete(`${backend_url}/core/api/alarm-devices/${id}/`, {
        headers: getAuthHeader(),
      });
      dispatch(setSnackBar("Alarm device deleted successfully"));
      return id;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to delete alarm device",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const testAlarmConnection = createAsyncThunk(
  "alarm/testAlarmConnection",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.post(
        `${backend_url}/core/api/alarm-devices/${id}/test_connection/`,
        {},
        { headers: getAuthHeader() },
      );
      dispatch(
        setSnackBar(response.data.message || "Connection test successful"),
      );
      return { id, data: response.data };
    } catch (error) {
      dispatch(
        setSnackBar(error.response?.data?.error || "Connection test failed"),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const sendAlarmCommand = createAsyncThunk(
  "alarm/sendAlarmCommand",
  async (
    {
      id,
      deviceId, // Add this
      command,
      duration = 180,
      cameraId = null,
      eventUuid = null,
      triggerType = "MANUAL_TEST",
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      dispatch(showLoadingScreen(true));

      const payload = {
        command,
        duration: command === "ON" ? duration : undefined,
        camera_id: cameraId,
        trigger_type: triggerType,
        device_id: deviceId, // Send the actual device_id
      };

      if (eventUuid) {
        payload.event_uuid = eventUuid;
      }

      console.log(
        `Sending command to device ${deviceId} (ID: ${id}) with payload:`,
        payload,
      );

      const response = await axios.post(
        `${backend_url}/core/api/alarm-devices/${id}/send_command/`,
        payload,
        { headers: getAuthHeader() },
      );

      dispatch(setSnackBar(`Command ${command} sent successfully`));
      return { id, command, data: response.data };
    } catch (error) {
      console.error("Send command error:", error.response?.data);
      dispatch(
        setSnackBar(error.response?.data?.error || "Failed to send command"),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const fetchAlarmAssignments = createAsyncThunk(
  "alarm/fetchAlarmAssignments",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${backend_url}/core/api/alarm-assignments/`,
        {
          headers: getAuthHeader(),
          params,
        },
      );
      console.log("response of assignments", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to fetch assignments",
        ),
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const createAlarmAssignment = createAsyncThunk(
  "alarm/createAlarmAssignment",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.post(
        `${backend_url}/core/api/alarm-assignments/`,
        data,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      dispatch(setSnackBar("Camera assigned to alarm successfully"));
      return response.data;
    } catch (error) {
      console.error("Assignment error:", error.response?.data);
      dispatch(
        setSnackBar(error.response?.data?.error || "Failed to assign camera"),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const updateAlarmAssignment = createAsyncThunk(
  "alarm/updateAlarmAssignment",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      const response = await axios.patch(
        `${backend_url}/core/api/alarm-assignments/${id}/`,
        data,
        {
          headers: getAuthHeader(),
        },
      );
      dispatch(setSnackBar("Assignment updated successfully"));
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to update assignment",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const deleteAlarmAssignment = createAsyncThunk(
  "alarm/deleteAlarmAssignment",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoadingScreen(true));
      await axios.delete(`${backend_url}/core/api/alarm-assignments/${id}/`, {
        headers: getAuthHeader(),
      });
      dispatch(setSnackBar("Assignment deleted successfully"));
      return id;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to delete assignment",
        ),
      );
      return rejectWithValue(error.response?.data);
    } finally {
      dispatch(showLoadingScreen(false));
    }
  },
);

export const fetchAlarmLogs = createAsyncThunk(
  "alarm/fetchAlarmLogs",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(`${backend_url}/core/api/alarm-logs/`, {
        headers: getAuthHeader(),
        params,
      });
      console.log("response of trigger logs", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to fetch alarm logs",
        ),
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchActiveAlarms = createAsyncThunk(
  "alarm/fetchActiveAlarms",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${backend_url}/core/api/active-alarms/`,
        {
          headers: getAuthHeader(),
          params,
        },
      );
      console.log("response of active alarms", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to fetch active alarms",
        ),
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchCamerasWithAlarm = createAsyncThunk(
  "alarm/fetchCamerasWithAlarm",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${backend_url}/core/api/cameras-with-alarm/`,
        {
          headers: getAuthHeader(),
          params,
        },
      );
      console.log("response of cameras with alarms", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        setSnackBar(
          error.response?.data?.error || "Failed to fetch cameras with alarm",
        ),
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

// Initial State
const initialState = {
  devices: {
    results: [],
    count: 0,
    loading: false,
  },
  assignments: {
    results: [],
    count: 0,
    loading: false,
  },
  logs: {
    results: [],
    count: 0,
    loading: false,
  },
  activeAlarms: [],
  camerasWithAlarm: [],
  selectedDevice: null,
  selectedAssignment: null,
  error: null,
};

// Slice
const alarmSlice = createSlice({
  name: "alarm",
  initialState,
  reducers: {
    setSelectedDevice: (state, action) => {
      state.selectedDevice = action.payload;
    },
    setSelectedAssignment: (state, action) => {
      state.selectedAssignment = action.payload;
    },
    clearAlarmState: (state) => {
      state.selectedDevice = null;
      state.selectedAssignment = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Alarm Devices
      .addCase(fetchAlarmDevices.pending, (state) => {
        state.devices.loading = true;
      })
      .addCase(fetchAlarmDevices.fulfilled, (state, action) => {
        state.devices.loading = false;
        state.devices.results = action.payload.results || action.payload;
        state.devices.count = action.payload.count || action.payload.length;
      })
      .addCase(fetchAlarmDevices.rejected, (state, action) => {
        state.devices.loading = false;
        state.error = action.payload;
      })

      // Create Alarm Device
      .addCase(createAlarmDevice.fulfilled, (state, action) => {
        state.devices.results.unshift(action.payload);
        state.devices.count += 1;
      })

      // Update Alarm Device
      .addCase(updateAlarmDevice.fulfilled, (state, action) => {
        const index = state.devices.results.findIndex(
          (d) => d.id === action.payload.id,
        );
        if (index !== -1) {
          state.devices.results[index] = action.payload;
        }
        if (state.selectedDevice?.id === action.payload.id) {
          state.selectedDevice = action.payload;
        }
      })

      // Delete Alarm Device
      .addCase(deleteAlarmDevice.fulfilled, (state, action) => {
        state.devices.results = state.devices.results.filter(
          (d) => d.id !== action.payload,
        );
        state.devices.count -= 1;
        if (state.selectedDevice?.id === action.payload) {
          state.selectedDevice = null;
        }
      })

      // Test Connection
      .addCase(testAlarmConnection.fulfilled, (state, action) => {
        const device = state.devices.results.find(
          (d) => d.id === action.payload.id,
        );
        if (device) {
          device.last_connected_at = new Date().toISOString();
        }
      })

      // Send Command
      .addCase(sendAlarmCommand.fulfilled, (state, action) => {
        const device = state.devices.results.find(
          (d) => d.id === action.payload.id,
        );
        if (device) {
          device.last_command_sent = action.payload.command;
          device.last_command_time = new Date().toISOString();
        }
      })

      // Fetch Assignments
      .addCase(fetchAlarmAssignments.pending, (state) => {
        state.assignments.loading = true;
      })
      .addCase(fetchAlarmAssignments.fulfilled, (state, action) => {
        state.assignments.loading = false;
        state.assignments.results = action.payload.results || action.payload;
        state.assignments.count = action.payload.count || action.payload.length;
      })
      .addCase(fetchAlarmAssignments.rejected, (state, action) => {
        state.assignments.loading = false;
        state.error = action.payload;
      })

      // Create Assignment
      .addCase(createAlarmAssignment.fulfilled, (state, action) => {
        state.assignments.results.unshift(action.payload);
        state.assignments.count += 1;
      })

      // Update Assignment
      .addCase(updateAlarmAssignment.fulfilled, (state, action) => {
        const index = state.assignments.results.findIndex(
          (a) => a.id === action.payload.id,
        );
        if (index !== -1) {
          state.assignments.results[index] = action.payload;
        }
      })

      // Delete Assignment
      .addCase(deleteAlarmAssignment.fulfilled, (state, action) => {
        state.assignments.results = state.assignments.results.filter(
          (a) => a.id !== action.payload,
        );
        state.assignments.count -= 1;
      })

      // Fetch Logs
      .addCase(fetchAlarmLogs.fulfilled, (state, action) => {
        state.logs.results = action.payload.results || action.payload;
        state.logs.count = action.payload.count || action.payload.length;
      })

      // Fetch Active Alarms
      .addCase(fetchActiveAlarms.fulfilled, (state, action) => {
        state.activeAlarms = action.payload.results || action.payload;
      })

      // Fetch Cameras With Alarm
      .addCase(fetchCamerasWithAlarm.fulfilled, (state, action) => {
        state.camerasWithAlarm = action.payload.results || action.payload;
      });
  },
});

// Selectors
export const selectAlarmDevices = (state) => state.alarm.devices;
export const selectAlarmAssignments = (state) => state.alarm.assignments;
export const selectAlarmLogs = (state) => state.alarm.logs;
export const selectActiveAlarms = (state) => state.alarm.activeAlarms;
export const selectCamerasWithAlarm = (state) => state.alarm.camerasWithAlarm;
export const selectSelectedDevice = (state) => state.alarm.selectedDevice;
export const selectSelectedAssignment = (state) =>
  state.alarm.selectedAssignment;

export const { setSelectedDevice, setSelectedAssignment, clearAlarmState } =
  alarmSlice.actions;
export default alarmSlice.reducer;
